/**
 * Shared (client-safe) types and helpers for Apple App Store / Google Play
 * subscriptions. Stripe stays the processor for the web app; the mobile
 * wrappers must use the platform stores, per App Store / Play policy.
 */
export type StorePlatform = "apple" | "google";

export type StoreStatus =
  | "trialing"
  | "active"
  | "grace"
  | "paused"
  | "expired"
  | "canceled"
  | "revoked";

/**
 * In-app product identifiers configured in App Store Connect / Play Console.
 * Apple uses reverse-DNS identifiers; Play keeps the existing ids.
 */
export const APPLE_PRODUCT_IDS = {
  monthly: "com.inquira.premium.monthly",
  yearly: "com.inquira.premium.yearly",
} as const;

export const GOOGLE_PRODUCT_IDS = {
  monthly: "inquira_premium_monthly",
  yearly: "inquira_premium_yearly",
} as const;

/** Default (Apple) product ids — Apple is the reviewed submission target. */
export const STORE_PRODUCT_IDS = APPLE_PRODUCT_IDS;

/** Human-readable product names shown on the paywall (App Review 3.1.2). */
export const PRODUCT_DISPLAY_NAMES = {
  monthly: "Inquira Premium Monthly",
  yearly: "Inquira Premium Yearly",
} as const;

export const PRODUCT_DURATIONS = {
  monthly: "1 month / Monthly subscription",
  yearly: "1 year / Yearly subscription",
} as const;

/** Apple's standard EULA — required Terms of Use link on the paywall. */
export const APPLE_EULA_URL = "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";
/** Inquira's published privacy policy. */
export const PRIVACY_POLICY_URL = "https://www.inquira.online/privacy";

/** Store-specific product id for the running platform. */
export function storeProductId(plan: "monthly" | "yearly" = "monthly"): string {
  return nativePlatform() === "google" ? GOOGLE_PRODUCT_IDS[plan] : APPLE_PRODUCT_IDS[plan];
}

/** Maps a store product id onto the plan key the rest of the app uses. */
export function planKeyForStoreProduct(productId: string): "inquira_pro_monthly" | "inquira_pro_yearly" {
  return /year|annual/i.test(productId) ? "inquira_pro_yearly" : "inquira_pro_monthly";
}

export const ACTIVE_STORE_STATUSES: readonly StoreStatus[] = ["trialing", "active", "grace"];

export interface NativeBillingBridge {
  /** Starts the platform purchase sheet and resolves with a verifiable receipt. */
  purchase(productId: string): Promise<{ platform: StorePlatform; receipt: string; productId: string }>;
  /** Re-reads owned entitlements from the store (Restore Purchases). */
  restore?(): Promise<Array<{ platform: StorePlatform; receipt: string; productId: string }>>;
  /** Opens the OS-level subscription management screen. */
  manageSubscriptions?(): Promise<void>;
  /**
   * StoreKit / Play introductory-offer eligibility for a product. Apple owns
   * this decision — the app must never infer or re-grant a trial itself.
   */
  introOfferEligibility?(productId: string): Promise<boolean>;
  /**
   * Optional store metadata for a product (localized price and, when Apple
   * reports one, the introductory offer attached to it).
   */
  productInfo?(productId: string): Promise<{
    productId: string;
    displayPrice?: string;
    introOffer?: { periodUnit?: string; periodCount?: number; isFreeTrial?: boolean } | null;
    /** Apple/Google verified expiry when the account already owns the product. */
    storeExpiresAt?: string | null;
  }>;
}

declare global {
  interface Window {
    InquiraNative?: NativeBillingBridge;
  }
}

/** True when running inside the iOS/Android wrapper that exposes store billing. */
export function getNativeBilling(): NativeBillingBridge | null {
  if (typeof window === "undefined") return null;
  const bridge = window.InquiraNative;
  return bridge && typeof bridge.purchase === "function" ? bridge : null;
}

export function isNativeStoreApp(): boolean {
  return getNativeBilling() !== null;
}

/**
 * Platform reported by Capacitor, independent of the billing plugin. Used to
 * decide that a purchase MUST go through the store even before the billing
 * bridge finished installing — never route an iOS purchase to Stripe.
 */
export function nativePlatform(): StorePlatform | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string; isNativePlatform?: () => boolean } })
    .Capacitor;
  const name = cap?.getPlatform?.();
  if (name === "ios") return "apple";
  if (name === "android") return "google";
  return null;
}

/** True inside the native iOS/Android wrapper (regardless of billing plugin). */
export function isNativeApp(): boolean {
  return nativePlatform() !== null;
}

/** True when digital purchases must go through the platform store. */
export function mustUseStoreBilling(): boolean {
  return isNativeApp() || isNativeStoreApp();
}

/**
 * Whether a purchase may fall back to the web (Stripe) checkout when the native
 * billing bridge is unavailable. Apple forbids external purchase flows inside
 * the iOS app, so iOS never falls back; Android/Play wrappers (and any wrapper
 * shipped without the billing plugin) must not leave the user unable to pay.
 */
export function canFallBackToWebCheckout(): boolean {
  return nativePlatform() !== "apple" && !isNativeStoreApp();
}

/**
 * True when the customer's subscription is managed by a platform store, so the
 * app must defer cancel / plan changes to the OS instead of Stripe.
 */
export function isStoreManagedBilling(): boolean {
  return isNativeStoreApp() || nativePlatform() === "apple";
}

export interface BillingBridgeHealth {
  /** `web` outside the wrapper, `ok` when the plugin answered, otherwise degraded. */
  state: "web" | "ok" | "missing_bridge" | "blocked";
  platform: StorePlatform | null;
  /** True when a web (Stripe) checkout is an acceptable payment path. */
  webFallbackAvailable: boolean;
  /** Short, user-facing explanation of the current state. */
  message: string;
}

/**
 * Runtime health check for the native billing plugin. Used by the paywall
 * diagnostics screen so a wrapper build shipped without `InquiraBilling` shows
 * a clear error state (and, off iOS, a safe web checkout fallback) instead of
 * an endless spinner.
 */
export async function checkBillingBridgeHealth(timeoutMs = 4000): Promise<BillingBridgeHealth> {
  const platform = nativePlatform();
  if (!platform && !isNativeStoreApp()) {
    return {
      state: "web",
      platform: null,
      webFallbackAvailable: true,
      message: "Running on the web — subscriptions are handled by card checkout.",
    };
  }
  const bridge = await waitForNativeBilling(timeoutMs);
  if (bridge) {
    return {
      state: "ok",
      platform,
      webFallbackAvailable: canFallBackToWebCheckout(),
      message: `${storeLabel(platform)} billing bridge is installed and responding.`,
    };
  }
  const fallback = canFallBackToWebCheckout();
  storeLog("bridge_health", { state: fallback ? "missing_bridge" : "blocked", platform });
  return {
    state: fallback ? "missing_bridge" : "blocked",
    platform,
    webFallbackAvailable: fallback,
    message: fallback
      ? `The ${storeLabel(platform)} billing plugin is missing from this build — purchases fall back to secure card checkout.`
      : `The ${storeLabel(platform)} billing plugin is missing from this build, and Apple does not allow an external checkout fallback. Update the app to subscribe.`,
  };
}

/**
 * Waits (briefly) for `window.InquiraNative` to appear. The Capacitor plugin
 * bridge can install a tick after first paint, and a paywall that read the
 * bridge synchronously used to fall back to a web checkout URL — which iOS
 * silently blocks, making Subscribe look unresponsive.
 */
export function waitForNativeBilling(timeoutMs = 4000): Promise<NativeBillingBridge | null> {
  const existing = getNativeBilling();
  if (existing || !isNativeApp()) return Promise.resolve(existing);
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const bridge = getNativeBilling();
      if (bridge) return resolve(bridge);
      if (Date.now() - started >= timeoutMs) return resolve(null);
      setTimeout(tick, 150);
    };
    tick();
  });
}

/** Store label for user-facing copy ("Manage in the App Store"). */
export function storeLabel(platform: StorePlatform | null | undefined): string {
  return platform === "apple" ? "App Store" : platform === "google" ? "Google Play" : "Stripe";
}

/**
 * Structured, non-sensitive billing log. Receipts, tokens and emails must never
 * be passed in — only ids, statuses and coarse error codes.
 */
export function storeLog(event: string, detail: Record<string, unknown> = {}): void {
  try {
    console.info(`[inquira.storekit] ${event}`, JSON.stringify(detail));
  } catch {
    console.info(`[inquira.storekit] ${event}`);
  }
}

/**
 * Opens an external URL so it works inside the native WebView too (the OS
 * browser / Safari View Controller rather than an in-app navigation that iOS
 * may block).
 */
export function openExternalUrl(url: string): void {
  if (typeof window === "undefined") return;
  const cap = (window as unknown as {
    Capacitor?: { Plugins?: { Browser?: { open?: (o: { url: string }) => Promise<void> } } };
  }).Capacitor;
  const browser = cap?.Plugins?.Browser;
  if (browser?.open) {
    void browser.open({ url });
    return;
  }
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) window.location.href = url;
}
